import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Box, Switch, Chip,
} from '@mui/material';
import { Add, Edit, Delete, LockReset } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface UserItem {
  id: number;
  tenant_id: number | null;
  username: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  is_superadmin: boolean;
  must_change_password: boolean;
  created_at: string | null;
}

const EMPTY_FORM = { username: '', email: '', password: '', full_name: '', role: 'admin' };

export default function UsersPage() {
  const { tenantId } = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resetDialog, setResetDialog] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/users`);
      setUsers(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (u: UserItem) => {
    setEditingId(u.id);
    setForm({ username: u.username, email: u.email, password: '', full_name: u.full_name || '', role: u.role });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/users/${editingId}`, {
          email: form.email, full_name: form.full_name || null, role: form.role,
        });
      } else {
        await api.post(`/api/tenants/${tenantId}/users`, {
          username: form.username, email: form.email, password: form.password,
          full_name: form.full_name || null, role: form.role,
        });
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this user?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/users/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (u: UserItem) => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleResetPassword = async () => {
    if (!tenantId || !resetDialog) return;
    try {
      await api.post(`/api/tenants/${tenantId}/users/${resetDialog}/reset-password`, { new_password: newPassword });
      setResetDialog(null);
      setNewPassword('');
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Reset failed');
    }
  };

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Users</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add User</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No users</TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u.id}>
                <TableCell>
                  {u.username}
                  {u.must_change_password && <Chip label="Password Reset Required" size="small" color="warning" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{u.full_name || '—'}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Chip label={u.role} size="small" /></TableCell>
                <TableCell><Switch checked={u.is_active} onChange={() => handleToggleActive(u)} size="small" /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => { setResetDialog(u.id); setNewPassword(''); }} title="Reset Password">
                    <LockReset fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          {!editingId && (
            <TextField label="Username" fullWidth margin="dense" value={form.username} onChange={updateField('username')} required />
          )}
          <TextField label="Email" fullWidth margin="dense" value={form.email} onChange={updateField('email')} required type="email" />
          {!editingId && (
            <TextField label="Password" fullWidth margin="dense" value={form.password} onChange={updateField('password')} required type="password"
              helperText="User will be required to change on first login" />
          )}
          <TextField label="Full Name" fullWidth margin="dense" value={form.full_name} onChange={updateField('full_name')} />
          <TextField label="Role" fullWidth margin="dense" value={form.role} onChange={updateField('role')}
            helperText="admin or user" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={editingId ? !form.email : (!form.username || !form.email || !form.password)}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetDialog !== null} onClose={() => setResetDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField label="New Password" fullWidth margin="dense" value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password"
            helperText="User will be required to change on next login" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={!newPassword}>Reset</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
